import React, { useState } from 'react';

import { Button, Stack, TextField } from '@mui/material';

import { TAG_ASSIGNMENT_ENTITY_ID_MAX_LENGTH } from '../../constants';

const EMPTY_HOTEL_OFFER_ID_ERROR = 'Enter a hotel offer ID';
const DUPLICATE_HOTEL_OFFER_ID_ERROR = 'This hotel offer ID is already added';
const HOTEL_OFFER_ID_TOO_LONG_ERROR = `Hotel offer ID must be ${TAG_ASSIGNMENT_ENTITY_ID_MAX_LENGTH} characters or fewer`;
const HOTEL_OFFER_ID_EXPLANATION =
  'Enter a hotel offer ID. IDs are not looked up or verified. Vendor and property IDs cannot be used here.';

interface Props {
  selectedIds: Array<string>;
  onAdd: (entityId: string) => void;
  disabled?: boolean;
}

export default function HotelIdEntry({ selectedIds, onAdd, disabled = false }: Props) {
  const [offerId, setOfferId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addHotelOfferId = () => {
    if (disabled) {
      return;
    }

    const trimmedOfferId = offerId.trim();

    if (!trimmedOfferId) {
      setError(EMPTY_HOTEL_OFFER_ID_ERROR);
      return;
    }

    if (trimmedOfferId.length > TAG_ASSIGNMENT_ENTITY_ID_MAX_LENGTH) {
      setError(HOTEL_OFFER_ID_TOO_LONG_ERROR);
      return;
    }

    if (selectedIds.includes(trimmedOfferId)) {
      setError(DUPLICATE_HOTEL_OFFER_ID_ERROR);
      return;
    }

    onAdd(trimmedOfferId);
    setOfferId('');
    setError(null);
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
      <TextField
        label="Hotel offer ID"
        value={offerId}
        onChange={(event) => {
          setOfferId(event.target.value);
          if (error) {
            setError(null);
          }
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') {
            return;
          }

          event.preventDefault();
          addHotelOfferId();
        }}
        error={!!error}
        helperText={error ?? HOTEL_OFFER_ID_EXPLANATION}
        disabled={disabled}
        fullWidth
        size="small"
      />
      <Button
        type="button"
        variant="contained"
        color="primary"
        onClick={addHotelOfferId}
        disabled={disabled}
        sx={{ flexShrink: 0 }}
      >
        Add hotel
      </Button>
    </Stack>
  );
}
